import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as themeConfig from './theme.config.js';

const ThemeModeContext = createContext({
  mode: 'system',
  resolvedMode: 'light',
  setMode: () => {},
  toggleMode: () => {},
});

export function useEchoHireTheme() {
  return useContext(ThemeModeContext);
}

export function EchoHireThemeProvider({ children }) {
  const [mode, setModeState] = useState(() => {
    try {
      return localStorage.getItem(themeConfig.STORAGE_KEY) || 'system';
    } catch {
      return 'system';
    }
  });

  const resolvedMode = useMemo(() => themeConfig.resolveThemeMode(mode), [mode]);

  useEffect(() => {
    themeConfig.applyCssTheme(mode);
  }, [mode, resolvedMode]);

  useEffect(() => {
    if (mode !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => themeConfig.applyCssTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  const setMode = useCallback((next) => {
    setModeState(next);
    try {
      localStorage.setItem(themeConfig.STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleMode = useCallback(() => {
    setMode(resolvedMode === 'dark' ? 'light' : 'dark');
  }, [resolvedMode, setMode]);

  const contextValue = useMemo(
    () => ({ mode, resolvedMode, setMode, toggleMode }),
    [mode, resolvedMode, setMode, toggleMode]
  );

  return <ThemeModeContext.Provider value={contextValue}>{children}</ThemeModeContext.Provider>;
}

export default EchoHireThemeProvider;
